import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Select, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Risk } from '../types/risk';
import riskService from '../api/riskService';
import RiskForm from './RiskForm';

const { Option } = Select;

interface RiskListProps {
  projectId?: number;
}

const RiskList: React.FC<RiskListProps> = ({ projectId }) => {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchRisks = React.useCallback(async () => {
    setLoading(true);
    try {
      const risksData = await riskService.getRisks(projectId);
      let filteredRisks = risksData;
      
      if (statusFilter !== 'all') {
        filteredRisks = risksData.filter(risk => risk.status === statusFilter);
      }
      
      setRisks(filteredRisks);
    } catch (error) {
      message.error('Failed to fetch risks');
    } finally {
      setLoading(false);
    }
  }, [projectId, statusFilter]);

  useEffect(() => {
    fetchRisks();
  }, [fetchRisks]);

  const handleDelete = async (riskId: number) => {
    try {
      await riskService.deleteRisk(riskId);
      message.success('Risk deleted successfully');
      fetchRisks();
    } catch (error) {
      message.error('Failed to delete risk');
    }
  };

  const getProbabilityColor = (probability: string) => {
    switch (probability) {
      case 'Low': return 'green';
      case 'Medium': return 'orange';
      case 'High': return 'red';
      default: return 'default';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'Low': return 'green';
      case 'Medium': return 'orange';
      case 'High': return 'red';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Identified': return 'blue';
      case 'Mitigated': return 'green';
      case 'Occurred': return 'red';
      case 'Closed': return 'gray';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Probability',
      dataIndex: 'probability',
      key: 'probability',
      render: (probability: string) => (
        <Tag color={getProbabilityColor(probability)}>
          {probability}
        </Tag>
      ),
    },
    {
      title: 'Impact',
      dataIndex: 'impact',
      key: 'impact',
      render: (impact: string) => (
        <Tag color={getImpactColor(impact)}>
          {impact}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Risk) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRisk(record);
              setShowForm(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this risk?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Option value="all">All Status</Option>
            <Option value="Identified">Identified</Option>
            <Option value="Mitigated">Mitigated</Option>
            <Option value="Occurred">Occurred</Option>
            <Option value="Closed">Closed</Option>
          </Select>
        </Space>
        
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRisk(null);
            setShowForm(true);
          }}
        >
          Add Risk
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={risks}
        rowKey="id"
        loading={loading}
        scroll={{ x: 800 }}
      />

      <RiskForm
        visible={showForm}
        risk={editingRisk || undefined}
        projectId={projectId}
        onCancel={() => {
          setShowForm(false);
          setEditingRisk(null);
        }}
        onSuccess={() => {
          setShowForm(false);
          setEditingRisk(null);
          fetchRisks();
        }}
      />
    </div>
  );
};

export default RiskList;